import { useSyncExternalStore } from "react";
import { stressStore } from "../store";

// ---- Guided narrated auto-tour ----------------------------------------------

export const TOUR_STEPS = [
  {
    id: "overture",
    title: "Overture",
    caption:
      "Academic Feudalism — a living map of the higher-education labor pyramid. Let me walk you through its tiers.",
    cam: { pos: [8, 5, 10], target: [0, 2.2, 0] },
    focusTier: null,
    stress: { phd: 0.15, budget: 0.1, funding: 0.1 },
  },
  {
    id: "base",
    title: "The Base · Adjuncts & TAs",
    caption:
      "At the base: adjuncts and teaching assistants. Roughly three in four faculty are now contingent, often paid about three thousand five hundred dollars per course.",
    cam: { pos: [4.6, 1.5, 5.6], target: [0, 0.7, 0] },
    focusTier: "base",
    stress: { phd: 0.4, funding: 0.35 },
  },
  {
    id: "middle",
    title: "The Middle · Tenure",
    caption:
      "Above them, the shrinking tenured class. Only about a quarter of faculty hold secure, tenure-track lines.",
    cam: { pos: [4.9, 2.9, 5.2], target: [0, 2.1, 0] },
    focusTier: "middle",
  },
  {
    id: "apex",
    title: "The Apex · Administration",
    caption:
      "At the apex: administration. Management roles have grown some sixty percent, while top presidents earn over a million dollars a year.",
    cam: { pos: [3.6, 4.5, 4.7], target: [0, 3.7, 0] },
    focusTier: "apex",
  },
  {
    id: "vault",
    title: "The Publisher Vault",
    caption:
      "Above it all floats the Publisher Vault — a stream of largely unpaid academic labor flowing upward, and out, as profit.",
    cam: { pos: [3.8, 5.6, 4.7], target: [0, 5.1, 0] },
    focusTier: null,
  },
  {
    id: "crisis",
    title: "The Crisis",
    caption:
      "Now watch what happens as funding is cut and PhD production surges. The base swells, and the entire room slides into crisis red.",
    cam: { pos: [9, 4.8, 11], target: [0, 2.4, 0] },
    focusTier: null,
    stress: { phd: 1, budget: 0.9, funding: 1 },
  },
  {
    id: "close",
    title: "Academic Feudalism",
    caption:
      "One pyramid. A vast base of serfs. A gilded few at the top. This is academic feudalism.",
    cam: { pos: [7, 4.6, 9], target: [0, 2.4, 0] },
    focusTier: null,
  },
];

const DEFAULT_CAM = { pos: [8, 5, 10], target: [0, 2.2, 0] };

const state = {
  active: false,
  index: -1,
  cam: DEFAULT_CAM,
  focusTier: null,
  narrating: false,
};

const listeners = new Set();
const emit = () => listeners.forEach((l) => l());
let stepTimer = null;

export const tourStore = {
  getState: () => state,
  subscribe: (l) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

function speak(text, onEnd) {
  const synth = window.speechSynthesis;
  if (!synth) {
    onEnd && (stepTimer = setTimeout(onEnd, 6500));
    return;
  }
  synth.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.rate = 0.98;
  u.pitch = 1;
  u.volume = 1;
  // prefer a calm English voice if present
  const voices = synth.getVoices();
  const pick =
    voices.find((v) => /en(-|_)?GB/i.test(v.lang) && /female|Google UK/i.test(v.name)) ||
    voices.find((v) => /Google US English/i.test(v.name)) ||
    voices.find((v) => /^en/i.test(v.lang));
  if (pick) u.voice = pick;
  state.narrating = true;
  emit();
  u.onend = () => {
    state.narrating = false;
    emit();
    onEnd && onEnd();
  };
  u.onerror = () => {
    state.narrating = false;
    onEnd && onEnd();
  };
  synth.speak(u);
}

function applyStep(i) {
  clearTimeout(stepTimer);
  const step = TOUR_STEPS[i];
  state.active = true;
  state.index = i;
  state.cam = step.cam;
  state.focusTier = step.focusTier ?? null;
  if (step.stress) stressStore.set(step.stress);
  emit();
  // advance when narration ends, with a fallback timer
  speak(step.caption, () => {
    stepTimer = setTimeout(() => nextStep(), 900);
  });
}

export function startTour() {
  applyStep(0);
}

export function nextStep() {
  clearTimeout(stepTimer);
  if (state.index + 1 < TOUR_STEPS.length) applyStep(state.index + 1);
  else stopTour();
}

export function prevStep() {
  clearTimeout(stepTimer);
  if (state.index > 0) applyStep(state.index - 1);
}

export function stopTour() {
  clearTimeout(stepTimer);
  if (window.speechSynthesis) window.speechSynthesis.cancel();
  state.active = false;
  state.index = -1;
  state.focusTier = null;
  state.narrating = false;
  state.cam = DEFAULT_CAM;
  emit();
}

export function useTour(selector) {
  return useSyncExternalStore(
    tourStore.subscribe,
    () => selector(state),
    () => selector(state)
  );
}
